/**
 * Edit Settings Modal - Sprint 19
 * Modal for editing scene layout, background, and script
 */

import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Button, Space, Typography, Alert } from 'antd';
import { Save, X } from 'lucide-react';
import { Scene, SceneBackground } from '../../../../types/scene';
import './EditSettingsModal.scss';

const { TextArea } = Input;
const { Text } = Typography;

interface EditSettingsModalProps {
  visible: boolean;
  scene: Scene | null;
  onClose: () => void;
  onSave: (sceneId: string, updates: Partial<Scene>) => Promise<void>;
}

const EditSettingsModal: React.FC<EditSettingsModalProps> = ({
  visible,
  scene,
  onClose,
  onSave,
}) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (visible && scene) {
      const rawBgType = scene.background?.type || 'solid';
      const bgType = rawBgType === 'color' ? 'solid' : rawBgType;
      const bgColors = rawBgType === 'color' 
        ? [scene.background?.value || '#1a1a1a'] 
        : (scene.background?.colors || ['#1a1a1a']);
      const bgImage = scene.background?.imageUrl || '';
      
      form.setFieldsValue({
        layout: scene.layoutPreset || 'screen-only',
        backgroundType: bgType,
        backgroundColor: bgType === 'solid' ? bgColors[0] : bgColors.join(', '),
        backgroundImage: bgImage,
        script: scene.script || '',
      });
    }
  }, [visible, scene, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (!scene) return;

      setSaving(true);

      // Build background object based on type
      const background: SceneBackground = {
        type: values.backgroundType,
      };

      if (values.backgroundType === 'solid') {
        background.colors = [values.backgroundColor];
      } else if (values.backgroundType === 'gradient') {
        // Parse gradient colors (comma-separated)
        background.colors = values.backgroundColor
          .split(',')
          .map((c: string) => c.trim())
          .filter((c: string) => c);
      } else if (values.backgroundType === 'image') {
        background.imageUrl = values.backgroundImage;
      }

      const updates: Partial<Scene> = {
        layoutPreset: values.layout,
        background,
        script: values.script,
      };

      await onSave(scene.id, updates);
      onClose();
    } catch (error) {
      console.error('[EditSettingsModal] Save failed:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const backgroundType = Form.useWatch('backgroundType', form);

  return (
    <Modal
      title={
        <Space>
          <Text strong>Edit Scene Settings</Text>
          {scene && <Text type="secondary">"{scene.title}"</Text>}
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      width={600}
      footer={[
        <Button key="cancel" onClick={handleCancel} icon={<X size={16} />}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={handleSave}
          loading={saving}
          icon={<Save size={16} />}
        >
          Save Changes
        </Button>,
      ]}
      className="edit-settings-modal"
    >
      {scene && (
        <>
          <Alert
            message="These changes will only affect this scene"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form
            form={form}
            layout="vertical"
            autoComplete="off"
          >
            {/* Layout Preset */}
            <Form.Item
              label="Layout Preset"
              name="layout"
              rules={[{ required: true, message: 'Please select a layout' }]}
            >
              <Select size="large">
                <Select.Option value="screen-only">Screen Only</Select.Option>
                <Select.Option value="webcam-only">Webcam Only</Select.Option>
                <Select.Option value="pip-bottom-right">PIP Bottom Right</Select.Option>
                <Select.Option value="pip-bottom-left">PIP Bottom Left</Select.Option>
                <Select.Option value="pip-top-right">PIP Top Right</Select.Option>
                <Select.Option value="pip-top-left">PIP Top Left</Select.Option>
                <Select.Option value="side-by-side">Side by Side</Select.Option>
                <Select.Option value="webcam-primary">Webcam Primary</Select.Option>
              </Select>
            </Form.Item>

            {/* Background Type */}
            <Form.Item
              label="Background Type"
              name="backgroundType"
              rules={[{ required: true, message: 'Please select a background type' }]}
            >
              <Select size="large">
                <Select.Option value="solid">Solid Color</Select.Option>
                <Select.Option value="gradient">Gradient</Select.Option>
                <Select.Option value="image">Image</Select.Option>
              </Select>
            </Form.Item>

            {/* Background Color/Gradient (conditional) */}
            {(backgroundType === 'solid' || backgroundType === 'gradient') && (
              <Form.Item
                label={backgroundType === 'solid' ? 'Background Color' : 'Gradient Colors'}
                name="backgroundColor"
                rules={[{ required: true, message: 'Please enter a color value' }]}
              >
                <Input
                  size="large"
                  placeholder={
                    backgroundType === 'solid'
                      ? '#1a1a1a'
                      : '#667eea, #764ba2'
                  }
                />
              </Form.Item>
            )}

            {/* Background Image URL (conditional) */}
            {backgroundType === 'image' && (
              <Form.Item
                label="Background Image URL"
                name="backgroundImage"
                rules={[
                  { required: true, message: 'Please enter an image URL' },
                  { type: 'url', message: 'Please enter a valid URL' },
                ]}
              >
                <Input
                  size="large"
                  placeholder="https://example.com/background.jpg"
                />
              </Form.Item>
            )}

            {/* Script */}
            <Form.Item
              label="Script"
              name="script"
              help="Optional: Add a script for teleprompter guidance"
            >
              <TextArea
                rows={6}
                placeholder="Enter your script here..."
                maxLength={5000}
                showCount
              />
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  );
};

export default EditSettingsModal;
